/**
 * End-of-run modal — Future Cartridge revamp (PR 5 of TODOs #24).
 *
 * Two modes share the same scene + UI vocabulary; the differences are
 * the headline copy/tint + which party slice we list:
 *
 *   mode='win'    — Brock defeated. Lists surviving party. --ok-green
 *                   headline. CTA: Reset Run.
 *   mode='defeat' — Full party wipe. Lists fallen party (from
 *                   params.casualties — runState is empty by this point
 *                   because permadeath already spliced everyone).
 *                   --err-red headline. CTA: Reset Run (forced — only
 *                   choice, no close button).
 *
 * DOM glass modal parented to #game replaces the Phaser-rendered
 * Rectangle + BitmapText chrome. m5x7 is retired from this scene.
 *
 * Lifecycle: BattleScene.scene.sleep() + scene.launch('EndRunScene')
 * opens us. resetRun() does location.reload() which tears down
 * everything naturally — SHUTDOWN cleanup of the DOM is registered
 * anyway as a hygiene measure for unanticipated scene swaps.
 */
import Phaser from 'phaser';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { audio } from '../systems/audio';
import type { SnapshotHandle } from '../render/snapshotBackdrop';

export interface EndRunSceneData {
  mode: 'win' | 'defeat';
  /** For defeat mode: the species + level of every Pokémon that fell
   *  this run. BattleScene captures this BEFORE removeFromParty splices
   *  them out, so the graveyard list is complete even though runState
   *  is empty by the time this scene reads it. */
  casualties?: Array<{ species: string; level: number }>;
  /** Glass-Over-World PR 2: BattleScene's snapshot, re-used here so the
   *  Battle→EndRun transition doesn't flash through a black backdrop.
   *  transferOwnership runs in `init` (not create) because BattleScene's
   *  SHUTDOWN follows on the next queue step — taking ownership in init
   *  moves the shutdown listener off BattleScene before its disposal
   *  would fire. Eng-locked #8 / design §EndRun handoff. */
  snapshotHandle?: SnapshotHandle | null;
}

function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

export class EndRunScene extends Phaser.Scene {
  private params!: EndRunSceneData;
  private modalRoot: HTMLDivElement | null = null;

  constructor() {
    super('EndRunScene');
  }

  init(data: EndRunSceneData): void {
    this.params = data;
    // Glass-Over-World PR 2: take ownership of BattleScene's snapshot
    // in init (not create) so the transfer beats BattleScene's
    // SHUTDOWN, which the SceneManager queues right behind this start
    // op. Without this ordering the shutdown listener would dispose
    // the DynamicTexture before we could re-parent the Image.
    if (data.snapshotHandle) {
      data.snapshotHandle.transferOwnership(this);
    }
  }

  create(): void {
    if (!this.params.snapshotHandle) {
      this.cameras.main.setBackgroundColor('#0E1014');
    }

    // Phase 15a: Brock victory keeps the gym-victory fanfare BattleScene
    // already started. Defeat = total silence — DESIGN.md "permadeath =
    // somber, not celebratory". Stop any carried-over battle music on
    // the defeat branch.
    if (this.params.mode === 'defeat') {
      audio.stopMusic(this, { fadeMs: 200 });
    }

    this.mountDOM();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'endrun-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute(
      'aria-label',
      this.params.mode === 'win' ? 'Run complete' : 'Run over',
    );

    const card = document.createElement('div');
    card.className = 'endrun-card';

    const headline = document.createElement('h1');
    headline.className = 'endrun-card__headline';
    headline.setAttribute('data-mode', this.params.mode);
    headline.textContent =
      this.params.mode === 'win' ? 'YOU DEFEATED BROCK' : 'YOUR PARTY HAS FALLEN';
    card.appendChild(headline);

    const subhead = document.createElement('p');
    subhead.className = 'endrun-card__subhead';
    subhead.textContent =
      this.params.mode === 'win' ? 'Run complete.' : 'The journey ends here.';
    card.appendChild(subhead);

    const sectionLabel = document.createElement('div');
    sectionLabel.className = 'endrun-card__section-label';
    sectionLabel.textContent = this.params.mode === 'win' ? 'SURVIVORS' : 'GRAVEYARD';
    card.appendChild(sectionLabel);

    const list = document.createElement('ul');
    list.className = 'endrun-card__list';
    const entries =
      this.params.mode === 'win'
        ? runState.getParty().map((m) => ({
            species: titleCase(m.species),
            level: m.level,
            stats: `Lv ${m.level} · ${m.hp.current}/${m.hp.max} HP`,
            state: 'alive' as const,
          }))
        : (this.params.casualties ?? []).map((c) => ({
            species: titleCase(c.species),
            level: c.level,
            stats: `Lv ${c.level} · fallen`,
            state: 'fallen' as const,
          }));

    if (entries.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'endrun-card__list-empty';
      empty.textContent = '(none)';
      list.appendChild(empty);
    } else {
      for (const e of entries) {
        const row = document.createElement('li');
        row.className = 'endrun-card__row';
        row.setAttribute('data-state', e.state);
        const species = document.createElement('span');
        species.className = 'endrun-card__species';
        species.textContent = e.species;
        const stats = document.createElement('span');
        stats.className = 'endrun-card__stats';
        stats.textContent = e.stats;
        row.appendChild(species);
        row.appendChild(stats);
        list.appendChild(row);
      }
    }
    card.appendChild(list);

    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'endrun-card__cta';
    cta.textContent = 'Reset Run';
    cta.setAttribute(
      'aria-label',
      'Reset Run — wipe save and start over',
    );
    cta.addEventListener('click', () => this.resetRun());
    card.appendChild(cta);

    root.appendChild(card);
    gameEl.appendChild(root);
    this.modalRoot = root;
  }

  private resetRun(): void {
    runState.reset();
    saveManager.clear();
    window.location.reload();
  }

  private teardown(): void {
    this.modalRoot?.remove();
    this.modalRoot = null;
  }
}
